import SendIcon from '@mui/icons-material/Send';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAccount } from '../../contexts/AccountContext';
import { usePricing } from '../../contexts/PricingContext';
import { useTeamLimits } from '../../hooks/useTeamLimits';
import { tenderInquiry } from '../../services/api';
import { getInquiryHistory, saveInquiryMessage } from '../../services/tenderAIService';
import { EstimatedCostChip } from '../common/EstimatedCostChip';

interface Message {
  id?: string;
  role: 'user' | 'model' | 'system';
  content: string;
  isError?: boolean;
}

interface TenderInquirySectionProps {
  tenderId: string;
  usedCount: number;
}

const TenderInquirySection: React.FC<TenderInquirySectionProps> = ({ tenderId, usedCount }) => {
  const { account } = useAccount();
  const { pricingSettings } = usePricing();
  const theme = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const { checkLimit } = useTeamLimits();
  const { isLimitReached } = checkLimit('sectionChats', usedCount);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!tenderId) {
        return;
      }
      setIsInitializing(true);
      try {
        // biome-ignore lint/suspicious/noExplicitAny: complex type
        const history = (await getInquiryHistory(tenderId)) as any as Message[];
        if (history.length > 0) {
          setMessages(history);
        } else {
          setMessages([
            {
              role: 'system',
              content: 'What would you like to know about this tender request?',
            },
          ]);
        }
      } catch (_err) {
        // ignore error
      } finally {
        setIsInitializing(false);
      }
    };
    fetchHistory();
  }, [tenderId]);

  useEffect(() => {
    // References to messages and isGenerating satisfy the exhaustive-deps linter
    if (chatContainerRef.current && (messages || isGenerating !== undefined)) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, isGenerating]);

  const saveMessageToFirestore = async (msg: Message) => {
    try {
      // biome-ignore lint/suspicious/noExplicitAny: complex type
      await saveInquiryMessage(tenderId, msg as any);
    } catch (_e) {
      // ignore error
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || isLimitReached || isGenerating || !account?.clientId) {
      return;
    }

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: inputValue.trim(),
    };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputValue('');
    setIsGenerating(true);
    await saveMessageToFirestore(userMessage);

    try {
      const historyPayload = newMessages
        .filter((msg) => msg.role !== 'system')
        .map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));

      const res = await tenderInquiry({
        tenderId,
        message: userMessage.content,
        history: historyPayload,
        clientId: account.clientId,
        teamId: account.teamId,
      });

      if (res.success && res.response) {
        const modelMsg: Message = { role: 'model', content: res.response };
        setMessages((prev) => [...prev, modelMsg]);
        await saveMessageToFirestore(modelMsg);
      } else {
        throw new Error('Failed to get response from AI');
      }
    } catch (_err) {
      const errorMsg: Message = {
        id: crypto.randomUUID(),
        role: 'system',
        content: 'I encountered an error processing your request. Please try again.',
        isError: true,
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 600,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Alert
        severity="info"
        sx={{ borderRadius: 0, borderBottom: '1px solid', borderColor: 'divider', py: 0.5 }}
      >
        <Typography variant="body1">
          You can ask questions about the contents of the original tender documents here. Your chat
          history is automatically saved to this tender.
        </Typography>
      </Alert>
      <Box
        ref={chatContainerRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 2,
          backgroundColor: 'background.default',
          scrollBehavior: 'smooth',
        }}
      >
        {isInitializing ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : (
          messages.map((msg, idx) => {
            const isUser = msg.role === 'user';
            const isSystem = msg.role === 'system';
            const isDarkMode = theme.palette.mode === 'dark';

            let bgColor = '';
            let textColor = '';

            if (isUser) {
              bgColor = isDarkMode
                ? theme.palette.grey[800]
                : theme.palette.bidlogiq?.taupe || '#D2B48C';
              textColor = isDarkMode ? theme.palette.common.white : 'black';
            } else if (isSystem) {
              bgColor = 'action.hover';
              textColor = 'text.primary';
            } else {
              bgColor = theme.palette.greyContrast;
              textColor = theme.palette.primary.contrastText;
            }

            return (
              <Box
                key={msg.id || idx}
                sx={{
                  mb: 2,
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    maxWidth: '80%',
                    bgcolor: bgColor,
                    color: textColor,
                    border: msg.role === 'system' ? '1px solid' : 'none',
                    borderColor: msg.role === 'system' ? 'divider' : 'transparent',
                    borderRadius: 2,
                    ...(msg.isError && { bgcolor: 'error.light', color: 'error.contrastText' }),
                  }}
                >
                  {msg.role === 'user' ? (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {msg.content}
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
                        '& ul, & ol': { m: 0, pl: 2, mb: 1 },
                        '& li': { mb: 0.5 },
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    </Box>
                  )}
                </Paper>
              </Box>
            );
          })
        )}
        {isGenerating && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      {isLimitReached && (
        <Alert severity="warning" sx={{ m: 1 }}>
          You have reached your section chat limit for this cycle.
        </Alert>
      )}

      <Box
        sx={{
          p: 2,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              size="small"
              placeholder={
                isLimitReached ? 'Limit reached' : 'Ask a question about the tender documents...'
              }
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={isGenerating || isLimitReached || isInitializing}
              multiline
              maxRows={4}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!inputValue.trim() || isGenerating || isLimitReached || isInitializing}
              sx={{ alignSelf: 'flex-end' }}
            >
              <SendIcon />
            </IconButton>
            <Tooltip
              title="Exact billing depends on the length and complexity of the tender documents and provided context."
              slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
            >
              <EstimatedCostChip
                estimate={pricingSettings?.estimates?.tender_inquiry || 'Est. $1.00 – $3.00'}
                sx={{ fontSize: '0.65rem', mb: 1 }}
              />
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TenderInquirySection;
